// Mastermind Meeting — your recruited mentors at a round table. Each one speaks
// at most once, and ONLY if one of their trigger conditions fired this week
// (low savings, low persistence, freedom < 30%, a dream achieved, a loss week, or
// idle cash). Recruit up to 4 of the 8. Unlocks at age 11.

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import type { Txn, MentorId } from '@biz-kid/engine';
import { computeMentorSignals, MENTOR_ROSTER, mastermindUnlocked } from '@biz-kid/engine';
import { useGameState } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip } from '../ui/kit.js';
import { useLife, useLifeComputed, useLifeDispatch } from './store.js';
import { selectAdvice, TRIGGER_LABEL } from './mentorAdvice.js';

const weekCashflow = (ledger: Txn[], w: number): number =>
  ledger.filter((t) => t.week === w).reduce((s, t) => (t.category === 'capital' ? s : s + t.amount), 0);

export function MastermindMeeting({ onClose }: { onClose: () => void }) {
  const s = useLife();
  const c = useLifeComputed();
  const dispatch = useLifeDispatch();
  const week = useGameState((g) => g.clock.week);
  const ledger = useGameState((g) => g.ledger);

  // Opening the meeting "reads" this week's advice — clears the cottage glow
  // in the world until next week brings fresh signals.
  useEffect(() => {
    dispatch({ type: 'MARK_MENTOR_READ', week });
  }, [week, dispatch]);

  const unlocked = mastermindUnlocked(s.age);
  const lastWeekCashflow = week > 1 ? weekCashflow(ledger, week - 1) : null;
  const savingsValue = s.assets.find((a) => a.type === 'savings')?.value ?? 0;

  const signals = computeMentorSignals({
    savingsValue,
    cash: s.cash,
    persistence: s.mindset.persistence,
    freedomPct: c.freedomPct,
    achievedDreams: s.visionBoard.filter((v) => v.achieved).length,
    lastWeekCashflow,
    totalAssetValue: c.totalAssetValue,
    cashIdleYears: s.cashIdleYears ?? 0,
  });

  const recruited = s.mastermind;
  const recruitedIds = new Set(recruited.map((m) => m.id));
  const full = recruited.length >= 4;

  const recruit = (m: (typeof MENTOR_ROSTER)[number]) => {
    if (full || recruitedIds.has(m.id)) return;
    dispatch({ type: 'ADD_MENTOR', mentor: { id: m.id, name: m.name, trait: m.specialty, icon: m.icon } });
  };

  // Who speaks this week?
  const speaking = recruited
    .map((m) => ({ mentor: m, advice: selectAdvice(m.id as MentorId, signals, week) }))
    .filter((x) => x.advice);

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 9, overflowY: 'auto' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">YOUR CREW · WEEK {week}</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>THE CREW</h1>
          </div>
          <Button tone="ghost" size="sm" onClick={onClose}>← HOME</Button>
        </header>

        {!unlocked ? (
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40 }}>🔒</div>
            <h2 style={{ margin: '6px 0 2px', fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>UNLOCKS AT AGE 11</h2>
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-2)', fontWeight: 700 }}>
              Keep grinding. Your crew is coming.
            </p>
          </Card>
        ) : (
          <>
            {/* the table */}
            {recruited.length === 0 ? (
              <Card style={{ textAlign: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 36 }}>🪑</div>
                <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text-2)', fontWeight: 700 }}>
                  No crew yet. RECRUIT up to 4 below.
                </p>
              </Card>
            ) : (
              <Card style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <Eyebrow>IN YOUR CREW</Eyebrow>
                  <Chip tone={speaking.length ? 'brand' : 'neutral'}>
                    {speaking.length ? `${speaking.length} speaking this week` : 'a calm week — all quiet'}
                  </Chip>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 10 }}>
                  {recruited.map((m) => {
                    const said = selectAdvice(m.id as MentorId, signals, week);
                    return (
                      <motion.div
                        key={m.id}
                        layout
                        style={{
                          borderRadius: 16,
                          padding: 14,
                          background: said ? 'var(--brand-soft)' : 'var(--surface-2)',
                          border: `1px solid ${said ? 'var(--brand)' : 'var(--border)'}`,
                          opacity: said ? 1 : 0.7,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 30 }}>{m.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{m.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{m.trait}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => dispatch({ type: 'REMOVE_MENTOR', id: m.id })}
                            aria-label="Dismiss"
                            style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 15 }}
                          >
                            ×
                          </button>
                        </div>
                        {said ? (
                          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                              {TRIGGER_LABEL[said.trigger]}
                            </div>
                            <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5, fontStyle: 'italic' }}>“{said.text}”</div>
                          </motion.div>
                        ) : (
                          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-3)' }}>Nods quietly — nothing to flag this week.</div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* recruit */}
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Eyebrow color="var(--brand)">BUILD YOUR CREW</Eyebrow>
                <Chip tone="brand">{recruited.length}/4 SPOTS</Chip>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginTop: 12 }}>
                {MENTOR_ROSTER.map((m) => {
                  const inCircle = recruitedIds.has(m.id);
                  const canRecruit = !inCircle && !full;
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: 10,
                        borderRadius: 12,
                        border: `1px solid ${inCircle ? 'var(--up)' : 'var(--border)'}`,
                        background: inCircle ? 'var(--up-soft)' : 'var(--surface-2)',
                        opacity: !inCircle && full ? 0.5 : 1,
                      }}
                    >
                      <span style={{ fontSize: 26 }}>{m.icon}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 800, fontSize: 13, color: 'var(--text)' }}>{m.name}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)' }}>{m.specialty}</span>
                      </span>
                      {inCircle ? (
                        <Button tone="ghost" size="sm" onClick={() => dispatch({ type: 'REMOVE_MENTOR', id: m.id })}>CUT</Button>
                      ) : (
                        <Button tone="primary" size="sm" disabled={!canRecruit} onClick={() => recruit(m)}>ADD</Button>
                      )}
                    </div>
                  );
                })}
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5, fontWeight: 700 }}>
                They speak up when it matters. Pick your crew.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
